import { motion } from "framer-motion";
import { ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import styles from "@components/buttons/Tabs.module.css";
import Icon from "@components/text/Icon";
import { generateHTMLId } from "@utils/htmlUtils";
import { IconName } from "@utils/iconUtils";

type ButtonProps<T> = {
  disabled?: boolean;
  icon?: IconName;
  textKey: string;
  value: T;
}[];

type Props<T> = {
  onChange: (t: T) => void;
  options: ButtonProps<T>;
  value: T;
};

const Tabs = <T extends string>({ onChange, options, value }: Props<T>) => {
  const intl = useIntl();

  return (
    <fieldset className={styles.tabs}>
      <div className={styles.tabContainer}>
        {options.map(option => {
          const id = `${option.value}-${generateHTMLId()}`;

          return (
            <div
              className={`${option.value === value ? styles.selectedTab : styles.unselectedTab} ${styles.tabContent} ${
                option.disabled ? styles.disabled : ""
              }`}
              key={option.value}
              onClick={() => onChange(option.value)}
            >
              <input
                aria-label={intl.formatMessage({ id: option.textKey }) ?? undefined}
                checked={option.value === value}
                className={styles.input}
                disabled={option.disabled}
                id={id}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value) {
                    onChange(event.target.value as T);
                  }
                }}
                type="radio"
                value={option.value !== null ? option.value : undefined}
              />
              <label className={`${styles.tabLabel} no-wrap`}>
                {option.icon && (
                  <Icon
                    className={styles.tabIcon}
                    color={option.value === value ? "primary" : "gray-300"}
                    icon={option.icon}
                    size="medium"
                  />
                )}
                <FormattedMessage id={option.textKey} />
              </label>
              {option.value === value ? <motion.div className={styles.indicator} layoutId="underline" /> : null}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

export default Tabs;
